import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Btn, StatCard, Spinner } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDate, PLAN_META } from "@/lib/format";
import { LogOut, ShieldAlert, Building2, Store, Plus, Trash2, Tag, Inbox, Receipt, Globe, Bell,
  Users, UserPlus, KeyRound, Pencil, Power, Copy, Shuffle, Eye, Activity, ChevronDown, ChevronUp } from "lucide-react";
import AdminRequests from "@/app/components/AdminRequests";
import AdminInvoices from "@/app/components/AdminInvoices";

const ROLES = ["owner", "admin", "employee"];

function genPassword(len = 12) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$";
  const a = new Uint32Array(len);
  crypto.getRandomValues(a);
  return Array.from(a, (n) => chars[n % chars.length]).join("");
}

const RoleBadge = ({ role }) => (
  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{role}</span>
);

const RES = { customers: "customer", leads: "lead", quotes: "quote", invoices: "invoice", jobs: "job", tasks: "task", expenses: "expense", materials: "material", products: "product", time: "time entry", business: "business settings", ai: "Alfie draft", files: "file" };
const VERB = { POST: "Created", PUT: "Updated", PATCH: "Updated", DELETE: "Deleted" };
function actionLabel(a) {
  const seg = (a.path || "").split("/").filter(Boolean); // ["api","customers",...]
  const res = RES[seg[1]] || seg[1] || "record";
  return `${VERB[a.method] || a.method} ${res}`;
}

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { data: stats, refetch: refetchStats } = useFetch("/admin/stats");
  const { data: biz, refetch: refetchBiz } = useFetch("/admin/businesses");
  const { data: users, refetch: refetchUsers } = useFetch("/admin/users");
  const { data: support, refetch: refetchSupport } = useFetch("/admin/support-access");
  const { data: services, refetch: refetchServices } = useFetch("/admin/services");
  const { data: pricing, refetch: refetchPricing } = useFetch("/admin/pricing");

  const [openFor, setOpenFor] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [tab, setTab] = useState("accounts");
  const [reason, setReason] = useState("");
  const [svcOpen, setSvcOpen] = useState(false);
  const [svc, setSvc] = useState({ title: "", description: "", price_from: 0, category: "General" });
  const [price, setPrice] = useState(null);
  useEffect(() => { if (pricing && !price) setPrice(JSON.parse(JSON.stringify(pricing))); }, [pricing]); // eslint-disable-line

  // account & user management state
  const [addOpen, setAddOpen] = useState(false);
  const [newClient, setNewClient] = useState({ business_name: "", owner_name: "", owner_email: "", password: "", plan: "free" });
  const [deleteBiz, setDeleteBiz] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [uSearch, setUSearch] = useState("");
  const [uBiz, setUBiz] = useState("");
  const [uRole, setURole] = useState("");
  const [insightsFor, setInsightsFor] = useState(null);
  const [insights, setInsights] = useState(null);
  const [planFilter, setPlanFilter] = useState("");

  const refetchAll = () => { refetchBiz(); refetchUsers(); refetchStats(); };

  const savePricing = async () => {
    try {
      await api.put("/admin/pricing", {
        currency: price.currency, vat_inclusive: price.vat_inclusive,
        website_addon_price: Number(price.website_addon_price),
        trial_days: Number(price.trial_days ?? 0),
        plans: {
          free: { ...price.plans.free, price: Number(price.plans.free.price), employee_limit: Number(price.plans.free.employee_limit) },
          basic: { ...price.plans.basic, price: Number(price.plans.basic.price), employee_limit: Number(price.plans.basic.employee_limit) },
          full: { ...price.plans.full, price: Number(price.plans.full.price), employee_limit: Number(price.plans.full.employee_limit) },
        },
      });
      toast.success("Pricing updated"); refetchPricing();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doLogout = async () => { await logout(); nav("/login"); };
  const setPlan = async (id, plan) => { await api.post(`/admin/businesses/${id}/plan`, { plan }); toast.success("Plan updated"); refetchBiz(); };
  const requestOpen = (b) => { setReason(""); setOpenFor(b); };
  const confirmOpenWorkspace = async () => {
    if (reason.trim().length < 5) return toast.error("Please give a clear reason (5+ chars)");
    try {
      const { data } = await api.post(`/admin/businesses/${openFor.id}/impersonate`, { reason: reason.trim() });
      const url = `${window.location.origin}/impersonate#t=${encodeURIComponent(data.access_token)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(`Support session started for ${data.business_name}`);
      setOpenFor(null); setReason(""); refetchSupport();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addService = async () => {
    if (!svc.title.trim()) return toast.error("Title required");
    try { await api.post("/admin/services", { ...svc, price_from: Number(svc.price_from) }); toast.success("Service published"); setSvcOpen(false); setSvc({ title: "", description: "", price_from: 0, category: "General" }); refetchServices(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delService = async (id) => { if (!window.confirm("Delete service?")) return; await api.delete(`/admin/services/${id}`); refetchServices(); };

  // ---- clients ----
  const createClient = async () => {
    try {
      const { data } = await api.post("/admin/businesses", newClient);
      toast.success(`Client "${data.business.name}" created`);
      setAddOpen(false);
      setNewClient({ business_name: "", owner_name: "", owner_email: "", password: "", plan: "free" });
      refetchAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleBiz = async (b) => {
    const active = b.active !== false;
    try {
      await api.post(`/admin/businesses/${b.id}/${active ? "deactivate" : "activate"}`);
      toast.success(active ? "Client deactivated" : "Client re-activated");
      refetchAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const confirmDeleteBiz = async () => {
    try {
      await api.delete(`/admin/businesses/${deleteBiz.id}`, { data: { confirm_name: deleteText } });
      toast.success(`Deleted ${deleteBiz.name}`);
      setDeleteBiz(null); setDeleteText(""); refetchAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openInsights = async (b) => {
    setInsightsFor(b); setInsights(null);
    try { const { data } = await api.get(`/admin/businesses/${b.id}/insights`); setInsights(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setInsightsFor(null); }
  };

  // ---- users (client-side filtering) ----
  const uList = useMemo(() => {
    let list = users?.items || [];
    if (uBiz) list = list.filter((u) => u.business_id === uBiz);
    if (uRole) list = list.filter((u) => u.role === uRole);
    if (uSearch) {
      const s = uSearch.toLowerCase();
      list = list.filter((u) => (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s));
    }
    return list;
  }, [users, uBiz, uRole, uSearch]);

  const saveUser = async () => {
    try {
      await api.put(`/admin/users/${editUser.id}`, { name: editUser.name, email: editUser.email, role: editUser.role });
      toast.success("User updated"); setEditUser(null); refetchAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/${u.disabled ? "activate" : "suspend"}`);
      toast.success(u.disabled ? "User re-activated" : "User suspended");
      refetchAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); refetchAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doReset = async () => {
    if (resetPwd.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await api.post(`/admin/users/${resetUser.id}/reset-password`, { password: resetPwd });
      toast.success(`Password set for ${resetUser.email}`);
      setResetUser(null); setResetPwd("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const copyText = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  if (!stats) return <div className="min-h-screen bg-slate-900"><Spinner /></div>;
  const businesses = biz?.items || [];
  const shownBusinesses = planFilter ? businesses.filter((b) => b.plan === planFilter) : businesses;
  const req = stats.requests || { website_new: 0, service_new: 0, contact_new: 0, total_new: 0 };
  const recentBiz = stats.recent_businesses || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-navy text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-extrabold text-lg">
            <span className="h-8 w-8 rounded-full overflow-hidden"><img src="/amden-logo.png?v=2" alt="Amden One" className="h-full w-full object-cover" /></span>
            Amden<span className="text-blue-400">One</span> <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-white/10 text-blue-200">Super Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-300 hidden sm:block">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout" className="p-2 rounded-lg hover:bg-white/10"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl font-bold text-navy font-display mb-6">Platform overview</h1>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Businesses" value={stats.businesses} icon={Building2} onClick={() => { setPlanFilter(""); setTab("accounts"); }} />
          <StatCard label="Users" value={stats.users} icon={Users} onClick={() => setTab("users")} />
          <StatCard label="Invoices" value={stats.invoices} icon={Receipt} onClick={() => setTab("invoices")} />
          <StatCard label="Website Requests" value={stats.website_requests} icon={Globe} onClick={() => setTab("requests")} />
        </div>
        <div className="grid grid-cols-3 gap-4 mb-8 max-w-md">
          {["free", "basic", "full"].map((p) => (
            <div key={p} onClick={() => { setPlanFilter(p); setTab("accounts"); }} data-testid={`plan-card-${p}`} className="amden-card p-4 text-center cursor-pointer hover:shadow-md hover:border-blue-200 transition-shadow"><p className="text-xs uppercase text-slate-400 font-semibold">{PLAN_META[p].name}</p><p className="text-2xl font-mono font-semibold text-navy mt-1">{stats.by_plan[p]}</p></div>
          ))}
        </div>

        {(req.total_new > 0 || recentBiz.length > 0) && (
          <div className="amden-card p-5 mb-8" data-testid="needs-attention">
            <div className="flex items-center gap-2 mb-3"><Bell size={16} className="text-amber-500" /><h3 className="font-semibold text-navy">Needs attention</h3></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <button onClick={() => setTab("requests")} data-testid="attention-website" className="text-left rounded-lg border border-slate-200 p-3 hover:border-blue-300 transition-colors"><p className="text-xs text-slate-400">New website requests</p><p className="text-xl font-semibold text-navy">{req.website_new}</p></button>
              <button onClick={() => setTab("requests")} data-testid="attention-service" className="text-left rounded-lg border border-slate-200 p-3 hover:border-blue-300 transition-colors"><p className="text-xs text-slate-400">New service enquiries</p><p className="text-xl font-semibold text-navy">{req.service_new}</p></button>
              <button onClick={() => setTab("requests")} data-testid="attention-contact" className="text-left rounded-lg border border-slate-200 p-3 hover:border-blue-300 transition-colors"><p className="text-xs text-slate-400">New contact messages</p><p className="text-xl font-semibold text-navy">{req.contact_new}</p></button>
              <button onClick={() => { setPlanFilter(""); setTab("accounts"); }} data-testid="attention-recent" className="text-left rounded-lg border border-slate-200 p-3 hover:border-blue-300 transition-colors"><p className="text-xs text-slate-400">Recently added</p><p className="text-sm font-medium text-navy truncate">{recentBiz[0]?.name || "—"}</p><p className="text-xs text-slate-400">{recentBiz.length} recent</p></button>
            </div>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="accounts" data-testid="admin-tab-accounts"><Building2 size={15} className="mr-1.5" /> Accounts</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users"><Users size={15} className="mr-1.5" /> Users</TabsTrigger>
            <TabsTrigger value="requests" data-testid="admin-tab-requests"><Inbox size={15} className="mr-1.5" /> Requests{req.total_new ? <span className="ml-1.5 text-[10px] bg-amber-500 text-white rounded-full px-1.5 py-0.5">{req.total_new}</span> : null}</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="admin-tab-invoices"><Receipt size={15} className="mr-1.5" /> Invoices</TabsTrigger>
            <TabsTrigger value="services" data-testid="admin-tab-services"><Store size={15} className="mr-1.5" /> Services</TabsTrigger>
            <TabsTrigger value="support" data-testid="admin-tab-support"><ShieldAlert size={15} className="mr-1.5" /> Support Access</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="admin-tab-pricing"><Tag size={15} className="mr-1.5" /> Pricing</TabsTrigger>
          </TabsList>

          {/* ACCOUNTS */}
          <TabsContent value="accounts">
            <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <p className="text-sm text-slate-500">{shownBusinesses.length} client business{shownBusinesses.length === 1 ? "" : "es"}</p>
                {planFilter && <button onClick={() => setPlanFilter("")} data-testid="clear-plan-filter" className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100">Plan: {PLAN_META[planFilter].name} ✕</button>}
              </div>
              <Btn variant="accent" onClick={() => { setNewClient({ business_name: "", owner_name: "", owner_email: "", password: genPassword(), plan: "free" }); setAddOpen(true); }} data-testid="admin-add-client"><UserPlus size={16} /> Add client</Btn>
            </div>
            <div className="amden-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr>
                  <th className="px-4 py-3 font-semibold">Business</th><th className="px-4 py-3 font-semibold hidden sm:table-cell">Owner</th>
                  <th className="px-4 py-3 font-semibold">Plan</th><th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Customers</th><th className="px-4 py-3 font-semibold text-right">Actions</th></tr></thead>
                <tbody>
                  {shownBusinesses.map((b) => {
                    const active = b.active !== false;
                    return (
                    <tr key={b.id} className="border-t border-slate-100" data-testid={`admin-business-${b.id}`}>
                      <td className="px-4 py-3 font-medium text-navy">{b.name}</td>
                      <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{b.owner_email}</td>
                      <td className="px-4 py-3">
                        <select value={b.plan} onChange={(e) => setPlan(b.id, e.target.value)} data-testid={`admin-plan-${b.id}`} className="rounded-lg border border-slate-300 px-2 py-1 text-sm bg-white">
                          {["free", "basic", "full"].map((p) => <option key={p} value={p}>{PLAN_META[p].name}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{active ? "Active" : "Deactivated"}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell font-mono text-slate-600">{b.customer_count}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1.5">
                          <button onClick={() => openInsights(b)} data-testid={`admin-insights-${b.id}`} title="Insights" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><Activity size={16} /></button>
                          <Btn variant="secondary" onClick={() => requestOpen(b)} data-testid={`admin-open-workspace-${b.id}`}><Eye size={14} /> Open workspace</Btn>
                          <button onClick={() => toggleBiz(b)} data-testid={`admin-toggle-biz-${b.id}`} title={active ? "Deactivate" : "Re-activate"} className={`p-2 rounded-lg ${active ? "text-amber-600 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}`}><Power size={16} /></button>
                          <button onClick={() => { setDeleteBiz(b); setDeleteText(""); }} data-testid={`admin-delete-biz-${b.id}`} title="Delete permanently" className="p-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  );})}
                  {shownBusinesses.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No client businesses{planFilter ? " on this plan" : ""} yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users">
            <div className="flex flex-wrap gap-2 mb-3">
              <input value={uSearch} onChange={(e) => setUSearch(e.target.value)} placeholder="Search name or email…" data-testid="admin-user-search" className="flex-1 min-w-[180px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              <select value={uBiz} onChange={(e) => setUBiz(e.target.value)} data-testid="admin-user-biz-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                <option value="">All businesses</option>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <select value={uRole} onChange={(e) => setURole(e.target.value)} data-testid="admin-user-role-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                <option value="">All roles</option>
                {ROLES.map((r) => <option key={r} value={r} className="capitalize">{r}</option>)}
              </select>
            </div>
            <div className="amden-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr>
                  <th className="px-4 py-3 font-semibold">Name</th><th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Business</th><th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold text-right">Actions</th></tr></thead>
                <tbody>
                  {uList.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100" data-testid={`admin-user-${u.id}`}>
                      <td className="px-4 py-3 font-medium text-navy">{u.name}</td>
                      <td className="px-4 py-3 text-slate-600">{u.email}</td>
                      <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{u.business_name || "—"}</td>
                      <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${u.disabled ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>{u.disabled ? "Suspended" : "Active"}</span></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setEditUser({ ...u })} data-testid={`admin-edit-user-${u.id}`} title="Edit" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><Pencil size={15} /></button>
                          <button onClick={() => { setResetUser(u); setResetPwd(genPassword()); }} data-testid={`admin-reset-user-${u.id}`} title="Reset password" className="p-2 rounded-lg text-blue-accent hover:bg-blue-50"><KeyRound size={15} /></button>
                          <button onClick={() => toggleUser(u)} data-testid={`admin-toggle-user-${u.id}`} title={u.disabled ? "Re-activate" : "Suspend"} className={`p-2 rounded-lg ${u.disabled ? "text-emerald-600 hover:bg-emerald-50" : "text-amber-600 hover:bg-amber-50"}`}><Power size={15} /></button>
                          <button onClick={() => deleteUser(u)} data-testid={`admin-delete-user-${u.id}`} title="Delete" className="p-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {uList.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No users match.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* REQUESTS */}
          <TabsContent value="requests">
            <AdminRequests onOpenWorkspace={(b) => requestOpen({ id: b.id, name: (businesses.find((x) => x.id === b.id) || {}).name })} />
          </TabsContent>

          {/* INVOICES (read-only, platform-wide) */}
          <TabsContent value="invoices">
            <AdminInvoices />
          </TabsContent>

          <TabsContent value="pricing">
            <div className="amden-card p-6 max-w-2xl">
              <h3 className="font-semibold text-navy mb-1">Subscription pricing</h3>
              <p className="text-sm text-slate-500 mb-4">Update prices without a code change. Applies to marketing, onboarding and workspaces instantly.</p>
              {price && (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div><label className="text-sm font-medium text-slate-700">Currency symbol</label>
                      <input value={price.currency} onChange={(e) => setPrice({ ...price, currency: e.target.value })} data-testid="pricing-currency" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
                    <div><label className="text-sm font-medium text-slate-700">Website add-on / month</label>
                      <input type="number" value={price.website_addon_price} onChange={(e) => setPrice({ ...price, website_addon_price: e.target.value })} data-testid="pricing-website" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" /></div>
                    <div><label className="text-sm font-medium text-slate-700">Free trial (days)</label>
                      <input type="number" value={price.trial_days ?? 0} onChange={(e) => setPrice({ ...price, trial_days: e.target.value })} data-testid="pricing-trial-days" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {["free", "basic", "full"].map((k) => (
                      <div key={k} className="rounded-lg border border-slate-200 p-3">
                        <p className="text-xs uppercase font-semibold text-slate-500 mb-2">{k}</p>
                        <label className="text-xs text-slate-500">Price/mo</label>
                        <input type="number" value={price.plans[k].price} data-testid={`pricing-${k}-price`}
                          onChange={(e) => setPrice({ ...price, plans: { ...price.plans, [k]: { ...price.plans[k], price: e.target.value } } })}
                          className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1.5 font-mono text-sm" />
                        <label className="text-xs text-slate-500 mt-2 block">Employee limit</label>
                        <input type="number" value={price.plans[k].employee_limit}
                          onChange={(e) => setPrice({ ...price, plans: { ...price.plans, [k]: { ...price.plans[k], employee_limit: e.target.value } } })}
                          className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1.5 font-mono text-sm" />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                    <div><p className="font-medium text-navy text-sm">Prices include VAT</p><p className="text-xs text-slate-500">Shows "VAT included" vs "VAT excluded" everywhere</p></div>
                    <button onClick={() => setPrice({ ...price, vat_inclusive: !price.vat_inclusive })} data-testid="pricing-vat-toggle" className={`h-6 w-11 rounded-full transition-colors ${price.vat_inclusive ? "bg-blue-600" : "bg-slate-300"}`}>
                      <span className={`block h-5 w-5 bg-white rounded-full transition-transform ${price.vat_inclusive ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </div>
                  <Btn variant="accent" onClick={savePricing} data-testid="pricing-save">Save pricing</Btn>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="services">
            <div className="flex justify-end mb-3"><Btn variant="accent" onClick={() => setSvcOpen(true)} data-testid="admin-add-service"><Plus size={16} /> Publish service</Btn></div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {(services?.items || []).map((s) => (
                <div key={s.id} className="amden-card p-5">
                  <div className="flex justify-between"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{s.category}</span><button onClick={() => delService(s.id)} className="text-slate-300 hover:text-red-600"><Trash2 size={15} /></button></div>
                  <h3 className="mt-2 font-semibold text-navy">{s.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{s.description}</p>
                  <p className="mt-3 font-mono font-semibold text-navy">{price?.currency || "£"}{s.price_from} from</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="support">
            <div className="space-y-3">
              {(support?.items || []).map((s) => {
                const acts = s.actions || [];
                const isOpen = !!expanded[s.id];
                return (
                  <div key={s.id} className="amden-card p-4" data-testid={`support-session-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-navy">{s.business_name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">By {s.acting_user}{s.as_email ? ` · as ${s.as_email}` : ""} · {fmtDate(s.created_at)}</p>
                        <p className="text-sm text-slate-600 mt-1"><span className="text-slate-400">Reason:</span> {s.reason}</p>
                      </div>
                      {s.business_id && <Btn variant="secondary" onClick={() => requestOpen({ id: s.business_id, name: s.business_name })} data-testid={`support-open-${s.id}`}><Eye size={14} /> Open again</Btn>}
                    </div>
                    <button onClick={() => setExpanded({ ...expanded, [s.id]: !isOpen })} data-testid={`support-toggle-${s.id}`} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-accent hover:underline">
                      {acts.length} change{acts.length === 1 ? "" : "s"} recorded {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                    {isOpen && (
                      <ul className="mt-2 border-t border-slate-100 pt-2 space-y-1" data-testid={`support-actions-${s.id}`}>
                        {acts.length === 0 && <li className="text-xs text-slate-400">No changes made — viewed only.</li>}
                        {acts.map((a, i) => (
                          <li key={i} className="flex items-center justify-between text-xs">
                            <span className="text-slate-600">{actionLabel(a)}</span>
                            <span className="text-slate-400 font-mono">{fmtDate(a.at)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
              {(support?.items || []).length === 0 && <div className="amden-card p-10 text-center text-slate-400" data-testid="support-empty">No support sessions recorded.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Open workspace (support session) dialog */}
      <Dialog open={!!openFor} onOpenChange={(o) => !o && setOpenFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Open workspace · {openFor?.name}</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-500">Give a quick reason. You'll enter this client's workspace in a new tab and can make changes — everything you do is recorded.</DialogDescription>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="support-reason" placeholder="Reason for opening this workspace…" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-2" />
          <DialogFooter><Btn variant="secondary" onClick={() => setOpenFor(null)}>Cancel</Btn><Btn variant="accent" onClick={confirmOpenWorkspace} data-testid="support-confirm"><Eye size={15} /> Open workspace</Btn></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add client dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add client account</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Create a new client business and its owner account.</DialogDescription>
          <div className="space-y-3 pt-1">
            <div><label className="text-xs text-slate-500">Business name</label>
              <input value={newClient.business_name} onChange={(e) => setNewClient({ ...newClient, business_name: e.target.value })} data-testid="add-client-business" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
            <div><label className="text-xs text-slate-500">Owner name</label>
              <input value={newClient.owner_name} onChange={(e) => setNewClient({ ...newClient, owner_name: e.target.value })} data-testid="add-client-name" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
            <div><label className="text-xs text-slate-500">Owner email</label>
              <input type="email" value={newClient.owner_email} onChange={(e) => setNewClient({ ...newClient, owner_email: e.target.value })} data-testid="add-client-email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
            <div><label className="text-xs text-slate-500">Starting plan</label>
              <select value={newClient.plan} onChange={(e) => setNewClient({ ...newClient, plan: e.target.value })} data-testid="add-client-plan" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white">
                {["free", "basic", "full"].map((p) => <option key={p} value={p}>{PLAN_META[p].name}</option>)}
              </select></div>
            <div><label className="text-xs text-slate-500">Initial password</label>
              <div className="mt-1 flex gap-2">
                <input value={newClient.password} onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} data-testid="add-client-password" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm" />
                <button onClick={() => setNewClient({ ...newClient, password: genPassword() })} title="Generate" className="px-3 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-50"><Shuffle size={15} /></button>
                <button onClick={() => copyText(newClient.password)} title="Copy" className="px-3 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-50"><Copy size={15} /></button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Share this with the owner — they can sign in immediately.</p>
            </div>
          </div>
          <DialogFooter><Btn variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={createClient} data-testid="add-client-save">Create client</Btn></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete business confirm */}
      <Dialog open={!!deleteBiz} onOpenChange={(o) => !o && setDeleteBiz(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Permanently delete client</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-600">This removes <b>{deleteBiz?.name}</b> and <b>all its data</b> — users, customers, jobs, quotes, invoices. This cannot be undone.</DialogDescription>
          <p className="text-sm text-slate-500 mt-3">Type the business name to confirm:</p>
          <input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} data-testid="delete-biz-confirm-input" placeholder={deleteBiz?.name} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
          <DialogFooter><Btn variant="secondary" onClick={() => setDeleteBiz(null)}>Cancel</Btn><Btn variant="danger" onClick={confirmDeleteBiz} disabled={deleteText !== deleteBiz?.name} data-testid="delete-biz-confirm">Delete permanently</Btn></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Edit this user's name, email and role.</DialogDescription>
          {editUser && (
            <div className="space-y-3 pt-1">
              <div><label className="text-xs text-slate-500">Name</label>
                <input value={editUser.name || ""} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="edit-user-name" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
              <div><label className="text-xs text-slate-500">Email</label>
                <input type="email" value={editUser.email || ""} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} data-testid="edit-user-email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
              <div><label className="text-xs text-slate-500">Role</label>
                <select value={editUser.role} onChange={(e) => setEditUser({ ...editUser, role: e.target.value })} data-testid="edit-user-role" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white capitalize">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="text-xs text-slate-400 mt-1">{editUser.business_name || "—"}. Employees can only clock in/out.</p></div>
            </div>
          )}
          <DialogFooter><Btn variant="secondary" onClick={() => setEditUser(null)}>Cancel</Btn><Btn variant="accent" onClick={saveUser} data-testid="edit-user-save">Save</Btn></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reset password · {resetUser?.email}</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-500">Set a new password and share it with the user. This is shown once — copy it now.</DialogDescription>
          <div className="mt-2 flex gap-2">
            <input value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} data-testid="reset-pwd-input" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm" />
            <button onClick={() => setResetPwd(genPassword())} title="Generate" className="px-3 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-50"><Shuffle size={15} /></button>
            <button onClick={() => copyText(resetPwd)} title="Copy" className="px-3 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-50"><Copy size={15} /></button>
          </div>
          <DialogFooter><Btn variant="secondary" onClick={() => setResetUser(null)}>Cancel</Btn><Btn variant="accent" onClick={doReset} data-testid="reset-pwd-save">Set password</Btn></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Client insights */}
      <Dialog open={!!insightsFor} onOpenChange={(o) => !o && setInsightsFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Insights · {insightsFor?.name}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Quick overview of this client's activity, users and revenue.</DialogDescription>
          {!insights ? <div className="py-8"><Spinner /></div> : (
            <div className="space-y-4 pt-1" data-testid="insights-body">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400">Users</p><p className="text-lg font-semibold text-navy">{insights.users.total}</p><p className="text-xs text-slate-500">{insights.users.active} active · {insights.users.by_role.owner || 0} owner / {insights.users.by_role.admin || 0} admin / {insights.users.by_role.employee || 0} staff</p></div>
                <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400">Revenue (paid)</p><p className="text-lg font-semibold text-navy font-mono">{insights.currency}{insights.revenue.paid_total}</p><p className="text-xs text-slate-500">{insights.currency}{insights.revenue.invoiced_total} invoiced</p></div>
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[["Customers", insights.counts.customers], ["Quotes", insights.counts.quotes], ["Invoices", insights.counts.invoices], ["Jobs", insights.counts.jobs]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-slate-50 p-2"><p className="text-sm font-semibold text-navy font-mono">{v}</p><p className="text-[11px] text-slate-400">{l}</p></div>
                ))}
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <span className="text-sm text-slate-500">Last activity</span>
                <span className="text-sm font-medium text-navy" data-testid="insights-last-activity">{insights.last_activity ? fmtDate(insights.last_activity) : "No activity yet"}</span>
              </div>
            </div>
          )}
          <DialogFooter><Btn variant="secondary" onClick={() => setInsightsFor(null)}>Close</Btn>{insightsFor && <Btn variant="accent" onClick={() => { const b = insightsFor; setInsightsFor(null); requestOpen(b); }} data-testid="insights-open-workspace"><Eye size={15} /> Open workspace</Btn>}</DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
